// Frank mirrors the picked ManufacturingLineList (plan) into the address bar so the selection survives a page
// refresh and the link can be shared. Each section owns one query parameter, manufacturing_line_<index> -- the
// SAME name and compact machine-id format the legacy Manufacturing Cost Calculator uses, so a link reads the
// same across both.
//
// Frank's plan key is the ManufacturingLineList#identifier: its manufacturing lines' identifiers joined by "|"
// (ManufacturingLine#identifier is "manufacturing_line:offset:<printId>:<dieId>" for an offset pair, or
// "manufacturing_line:flexo:<id>" for a combined flexo line). The address bar carries only the machine ids --
// everything after "manufacturing_line:<type>:" -- so it reads "manufacturing_line_0=10031-12008" instead of
// the percent-encoded identifier. The id COUNT recovers the type on the way back (2 ids -> offset pair, 1 id ->
// flexo), so the round trip reproduces the identifier exactly -- the same string the listing exposed and the
// solutions endpoint keys on.

const parameterName = (index) => `manufacturing_line_${index}`;

const MACHINE_ID_SEPARATOR = '-';
const LINE_SEPARATOR = '_';
const LINE_IDENTIFIER_SEPARATOR = '|';

// The machine ids inside a line identifier: everything after "manufacturing_line:<type>:".
const machineIdsOf = (lineIdentifier) => lineIdentifier.split(':').slice(2);

// Serialize one line identifier to its machine ids joined by "-" ("<printId>-<dieId>", or "<id>" for flexo).
const serializeLine = (lineIdentifier) => machineIdsOf(lineIdentifier).join(MACHINE_ID_SEPARATOR);

// Parse one token back to a line identifier, reconstructing the type from the machine id count (2 ids -> offset
// pair, 1 id -> flexo combined). Returns null for a malformed token (an empty or non-numeric id, or an id count
// other than 1 or 2) so a corrupt / stale-format param is rejected rather than restored as a bogus plan.
const parseLine = (token) => {
  const ids = token.split(MACHINE_ID_SEPARATOR);
  if (!ids.every((id) => id !== '' && !Number.isNaN(Number(id)))) { return null; }
  if (ids.length === 2) { return `manufacturing_line:offset:${ids[0]}:${ids[1]}`; }
  if (ids.length === 1) { return `manufacturing_line:flexo:${ids[0]}`; }
  return null;
};

// The URL value for a plan identifier: each line's machine ids, lines joined by "_". This round-trips exactly
// to the "|"-joined identifier the listing exposed, so the stored value and the plan key can never drift.
// Null / empty identifier yields an empty string.
export const manufacturingLineListParameterValue = (identifier) => (identifier || '')
  .split(LINE_IDENTIFIER_SEPARATOR)
  .filter(Boolean)
  .map(serializeLine)
  .join(LINE_SEPARATOR);

// The picked plan's identifier for this section (the "|"-joined line identifiers), or null when none is
// selected or the stored value is malformed. If ANY line fails to parse the whole param is treated as "no
// selection" (falls back to the listing) rather than restoring a partial / bogus plan.
export const readSelectedManufacturingLineList = (index) => {
  const value = new URLSearchParams(window.location.search).get(parameterName(index));
  if (!value) { return null; }
  const lines = value.split(LINE_SEPARATOR).map(parseLine);
  if (lines.some((line) => line === null)) { return null; }
  return lines.length > 0 ? lines.join(LINE_IDENTIFIER_SEPARATOR) : null;
};

// Keep the address bar in sync with this section's picked plan so a refresh / shared link reproduces it.
// replaceState (not pushState) is deliberate -- this is a mirror, not in-page navigation: it adds no history
// entries, so Back never lands mid-flow. A same-origin relative URL is used so only the query string changes.
export const writeSelectedManufacturingLineList = (index, identifier) => {
  const url = new URL(window.location.href);
  url.searchParams.set(parameterName(index), manufacturingLineListParameterValue(identifier));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// Remove this section's plan from the address bar (used by "back to machines" / a cleared selection).
export const clearSelectedManufacturingLineList = (index) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(parameterName(index));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// ---------------------------------------------------------------------------------------------------------
// THE SECTION'S FILTERS -- the bobbin, the machine types, the reductions and the area-ratio cap.
//
// They travel WITH the plan above, because a plan identifier only means anything against the listing it came
// from. Held only in component state they were lost the moment the section remounted -- which the guided setup
// wizard does on every close (the page's own ManufacturingLineLists is not mounted while the wizard is open), and
// a refresh / shared link does too. The section then came back on the server's DEFAULTS: the filter block no
// longer showed what the user had chosen, the picked plan was dropped as stale because the default listing does
// not hold it, and the whole section re-listed and re-scored, since its request URL no longer matched the one the
// wizard's answers are cached under (see ManufacturingLineResponseCache).
//
// One parameter per filter, each suffixed with the section index like manufacturing_line_<index> above:
//   printing_machine_types_0=digital     the machine types ticked, comma-joined
//   die_cutting_machine_types_0=         present-but-EMPTY means "nothing ticked" -- NOT the same as ABSENT,
//                                        which means "not spoken yet" and lets the server apply its own default
//   disabled_filters_0=smallest_die_cutting_machine_of_each_type   the reductions switched OFF
//   area_ratio_percent_0=25              the area-ratio cap
//   bobbin_0=true                        the bobbin tick
// A filter still at its default is left OUT rather than written, so an ordinary visitor's link stays as short as
// it was and "absent" keeps its meaning. The bobbin is written as soon as it is TOUCHED, including a false that
// happens to match the part_type's own default: what its default is depends on the part_type (carton starts
// unticked, carton_lamination ticked), so only "untouched" can be left unsaid.

const VALUE_SEPARATOR = ',';
const MACHINE_TYPES_KEY_SUFFIX = '_machine_types';
const DISABLED_FILTERS_NAME = 'disabled_filters';
const AREA_RATIO_PERCENT_NAME = 'area_ratio_percent';
const BOBBIN_NAME = 'bobbin';

const sectionParameterName = (name, index) => `${name}_${index}`;

// Set the parameter, or remove it when the value is null/undefined ("at its default, say nothing").
const setOrDelete = (url, name, value) => {
  if (value === null || value === undefined) {
    url.searchParams.delete(name);
    return;
  }
  url.searchParams.set(name, value);
};

const replaceQuery = (url) => window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

// A machine-type filter is recognised by its KEY, which the server names "<something>_machine_types"
// (printing_machine_types, die_cutting_machine_types). Keying off that convention rather than off a list here
// means a machine-type filter added server-side round-trips without having to be named here as well -- and it is
// applied on BOTH sides, so what this module writes is exactly what it can read back and clear again. A key that
// does not follow the convention is NOT persisted: writing one would leave a parameter that nothing restores and
// nothing ever removes, which is worse than not remembering it. Name a new filter to match and it works.
const isMachineTypesKey = (filterKey) => filterKey.endsWith(MACHINE_TYPES_KEY_SUFFIX);

// The machine-type parameter names this section holds. `_machine_types_1` cannot match `..._machine_types_11`:
// the whole suffix including the index must end the name.
const machineTypeParameterNames = (parameters, index) => [...parameters.keys()]
  .filter((name) => name.endsWith(`${MACHINE_TYPES_KEY_SUFFIX}_${index}`));

// The machine types this section's address bar holds, as { printing_machine_types: ['digital'] }. A filter row
// the user has not touched is simply absent, which is what the component's empty {} means too.
export const readSectionMachineTypes = (index) => {
  const parameters = new URLSearchParams(window.location.search);

  return machineTypeParameterNames(parameters, index).reduce((machineTypes, name) => {
    const value = parameters.get(name);
    const filterKey = name.slice(0, -`_${index}`.length);
    return { ...machineTypes, [filterKey]: value === '' ? [] : value.split(VALUE_SEPARATOR) };
  }, {});
};

// The reductions switched OFF for this section; [] when none is (the default).
export const readSectionDisabledFilters = (index) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(DISABLED_FILTERS_NAME, index));
  if (!value) { return []; }
  return value.split(VALUE_SEPARATOR).filter(Boolean);
};

// The area-ratio cap as a percent, or null when the address bar does not carry one (-> the page's own default).
// A corrupt parameter falls back to the default rather than listing against nonsense: non-numeric and negative
// are rejected like the input itself rejects them, and so is a value that is not FINITE -- "Infinity" and an
// overflowing "1e400" both survive Number.isNaN, and either would be handed to the listing, the solve and the
// scoring as the cap.
export const readSectionAreaRatioPercent = (index) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(AREA_RATIO_PERCENT_NAME, index));
  if (value === null || value.trim() === '') { return null; }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) { return null; }
  return parsed;
};

// The bobbin tick, or undefined when it has not been touched (-> the part_type's own default). Undefined and
// false are DIFFERENT answers here, which is why this returns undefined rather than false for an absent one.
export const readSectionBobbin = (index) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(BOBBIN_NAME, index));
  if (value === null) { return undefined; }
  return value === 'true';
};

// Mirror the section's filters into the address bar. Every machine-type parameter of this section is dropped
// first, so a row that goes back to "not spoken" leaves the URL instead of lingering. Pass null/undefined for a
// filter that is at its default -- the caller owns what its default is (the area-ratio cap comes from a window
// setting), so this module never has to guess it.
export const writeSectionFilters = (index, { machineTypes, disabledFilters, areaRatioPercent }) => {
  const url = new URL(window.location.href);

  machineTypeParameterNames(url.searchParams, index).forEach((name) => url.searchParams.delete(name));
  Object.entries(machineTypes || {})
    .filter(([filterKey]) => isMachineTypesKey(filterKey))
    .forEach(([filterKey, types]) => {
      url.searchParams.set(sectionParameterName(filterKey, index), types.join(VALUE_SEPARATOR));
    });

  setOrDelete(url, sectionParameterName(DISABLED_FILTERS_NAME, index),
    (disabledFilters || []).length > 0 ? disabledFilters.join(VALUE_SEPARATOR) : null);
  setOrDelete(url, sectionParameterName(AREA_RATIO_PERCENT_NAME, index),
    areaRatioPercent === null || areaRatioPercent === undefined ? null : String(areaRatioPercent));

  replaceQuery(url);
};

// Mirror the bobbin tick. It is written on its own because it belongs to the section ABOVE this listing (the
// material sheets are found over it, and the listing over those sheets) -- LayoutAndQuoteBuilderNavSection owns
// it, ManufacturingLineLists only draws its row.
export const writeSectionBobbin = (index, enabled) => {
  const url = new URL(window.location.href);
  setOrDelete(url, sectionParameterName(BOBBIN_NAME, index),
    enabled === null || enabled === undefined ? null : String(enabled));
  replaceQuery(url);
};
